import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProfileProvider } from "@/contexts/ProfileContext";
import { lazy, Suspense } from "react";
import { useGAPageView } from "@/hooks/useGAPageView";
import { BreadcrumbJsonLd } from "@/components/BreadcrumbJsonLd";
import Home from "./pages/Home.tsx";

// Lazy-load all non-homepage routes for code splitting
const Index = lazy(() => import("./pages/Index.tsx"));
const Calculators = lazy(() => import("./pages/Calculators.tsx"));

const RealEstate = lazy(() => import("./pages/RealEstate.tsx"));

const TaxSimulator = lazy(() => import("./pages/TaxSimulator.tsx"));
const Retirement = lazy(() => import("./pages/Retirement.tsx"));
const RentVsBuy = lazy(() => import("./pages/RentVsBuy.tsx"));
const DebtKiller = lazy(() => import("./pages/DebtKiller.tsx"));
const ExploreTools = lazy(() => import("./pages/ExploreTools.tsx"));
const Auth = lazy(() => import("./pages/Auth.tsx"));
const Profile = lazy(() => import("./pages/Profile.tsx"));
const Onboarding = lazy(() => import("./pages/Onboarding.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const StockResearch = lazy(() => import("./pages/StockResearch.tsx"));
const EMIMentor = lazy(() => import("./pages/EMIMentor.tsx"));
const DataRights = lazy(() => import("./pages/DataRights.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse.tsx"));
const LegalDisclaimers = lazy(() => import("./pages/LegalDisclaimers.tsx"));
const LifeStages = lazy(() => import("./pages/LifeStages.tsx"));
const CreditCardMaster = lazy(() => import("./pages/CreditCardMaster.tsx"));
const AssetComparison = lazy(() => import("./pages/AssetComparison.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));

const queryClient = new QueryClient();

function GAPageTracker() {
  useGAPageView();
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <AuthProvider>
          <ProfileProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <GAPageTracker />
              <BreadcrumbJsonLd />
              <Suspense fallback={<div className="min-h-screen" />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/onboarding" element={<Onboarding />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/dashboard" element={<Index />} />
                  <Route path="/calculators" element={<Calculators />} />
                  
                  <Route path="/real-estate" element={<RealEstate />} />
                  
                  <Route path="/tax-simulator" element={<TaxSimulator />} />
                  <Route path="/retirement" element={<Retirement />} />
                  <Route path="/rent-vs-buy" element={<RentVsBuy />} />
                  <Route path="/debt-killer" element={<DebtKiller />} />
                  <Route path="/tools" element={<ExploreTools />} />
                  <Route path="/stock-research" element={<StockResearch />} />
                  <Route path="/emi-mentor" element={<EMIMentor />} />
                  <Route path="/data-rights" element={<DataRights />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfUse />} />
                  <Route path="/legal-disclaimers" element={<LegalDisclaimers />} />
                  <Route path="/life-stages" element={<LifeStages />} />
                  <Route path="/credit-card-master" element={<CreditCardMaster />} />
                  <Route path="/asset-comparison" element={<AssetComparison />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ProfileProvider>
        </AuthProvider>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
